// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { isUndefined } from '@shared/utils';
import { useCallback, useEffect, useMemo } from 'react';
import {
  fetchAssetClasses,
  fetchAvailableUniverseData,
  fetchStaticRebalanceData,
  selectAvailableParametersAssetClasses,
  selectAvailableParametersUniverses,
} from '@advisor-app/store/slices/available-parameters';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import {
  getSavedPortfolios,
  getScreeners,
} from '@advisor-app/store/slices/my-research';
import { useResetAppStore } from './use-reset-app-store';
import { tickerTagApi } from '@shared/data-access/api/engine/ticker-tag-api';
import { TickerTagFeature } from '@shared/data-access/interfaces/api/engine/citec';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import {
  selectAccount,
  selectAccountType,
  selectBroker,
  selectBrokerConnected,
  setAccounts,
} from '@advisor-app/store/slices/broker';
import { type AccountInfo } from '@shared/data-access/interfaces/api/brokerage/account';
import { useAccounts } from '@advisor-app/features/accounts';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { portfolioApi } from '@advisor-app/store/api/brokerage/portfolio-api';
import { watchlistApi } from '@shared/data-access/api/engine/watchlist-api';
import { currencyApi } from '@shared/data-access/api/brokerage/currency-api';
import { buildInitialCurrencyPairs } from '@shared/ui-kit/utils/build-initial-currency-pairs';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { selectClientId } from '@shared/data-access/slices/clients';

export const useAppReduxLoader = () => {
  const dispatch = useAppDispatch();
  const runSafeUpdate = useSafeUpdate();
  const accountCurrency = useAppSelector(selectAccountCurrency);
  const clientId = useAppSelector(selectClientId);
  const universes = useAppSelector(selectAvailableParametersUniverses);
  const assetClasses = useAppSelector(selectAvailableParametersAssetClasses);

  const isUniversesOnRedux = useMemo(
    () => universes.length > 1,
    [universes.length],
  );

  const isAssetsLoaded = useMemo(
    () => assetClasses.length > 3,
    [assetClasses.length],
  );

  const isBrokerConnected = useAppSelector(selectBrokerConnected);
  const broker = useAppSelector(selectBroker);
  const accountType = useAppSelector(selectAccountType);
  const account = useAppSelector(selectAccount);

  const { getAccountsInfo, setAccountOnRedux } = useAccounts();

  const skipClientInstitutions = clientId === null || clientId === 0;

  advisoryApi.useGetAdvisoryInstitutionsQuery(
    {
      client_id: clientId ?? 0,
      currency: accountCurrency,
    },
    { skip: skipClientInstitutions },
  );

  const [getExchangeRates] = currencyApi.useLazyCurrencyPricesQuery();

  const { data: watchlistPositions } = watchlistApi.useGetWatchlistQuery();

  const { data: positions = [] } = portfolioApi.useGetPositionsQuery(
    {
      params: {
        broker_key: broker,
        type_account: accountType,
      },
      payload: {
        account_id: account.accountId,
      },
    },
    { skip: !isBrokerConnected || !account.accountId },
  );

  tickerTagApi.useSearchTickerTagQuery(
    { feature: TickerTagFeature.PortfolioPositions },
    { skip: !isBrokerConnected || positions.length === 0 },
  );

  tickerTagApi.useSearchTickerTagQuery({
    feature: TickerTagFeature.AggregatorPositions,
  });

  tickerTagApi.useSearchTickerTagQuery(
    { feature: TickerTagFeature.SelectedAssets },
    { skip: isUndefined(watchlistPositions) },
  );

  const getAccounts = useCallback(async () => {
    const accountsData = await getAccountsInfo(broker, accountType);

    runSafeUpdate(() => dispatch(setAccounts(accountsData)));

    return accountsData;
  }, [getAccountsInfo, broker, accountType, runSafeUpdate, dispatch]);

  const getExchangeRatesData = useCallback(async () => {
    await getExchangeRates(buildInitialCurrencyPairs()).unwrap();
  }, [getExchangeRates]);

  useResetAppStore();

  useEffect(() => {
    dispatch(getSavedPortfolios());
    dispatch(getScreeners());
    getExchangeRatesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isUniversesOnRedux) {
      setTimeout(() => {
        dispatch(fetchAvailableUniverseData());
        dispatch(fetchStaticRebalanceData());
      }, 500); // Timeout for Testing app.test.tsx
    }

    if (!isAssetsLoaded) dispatch(fetchAssetClasses());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universes]);

  useEffect(() => {
    if (isBrokerConnected) {
      getAccounts().then((accounts: AccountInfo[]) => {
        if (accounts?.length > 0 && !account.accountId) {
          runSafeUpdate(() => {
            setAccountOnRedux(accounts[0]);
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrokerConnected]);
};
